import { Form } from '@/components/ui/form';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { type AssetFormValues, assetSchema } from '@/lib/schemas';
import AssetService from '@/services/AssetService';
import type { AddressType } from '@/types/address';
import type { FormattedAsset } from '@/types/asset';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { AssetChecklist } from '../AssetChecklist';
import AddressSearchStep from './AddressSearchStep';
import FinancialDetailsStep, {
  type FinancialFormValues,
} from './FinancialDetailsStep';
import PropertyDetailsStep from './PropertyDetailsStep';

interface AssetFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAssetAdded?: () => void;
}

type WizardStep = 'address' | 'property' | 'financial' | 'checklist';

export default function AssetForm({
  open,
  onOpenChange,
  onAssetAdded,
}: AssetFormProps) {
  const [currentStep, setCurrentStep] = useState<WizardStep>('address');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<AddressType | null>(
    null,
  );
  const [assetData, setAssetData] = useState<FormattedAsset | null>(null);
  const [propertyValues, setPropertyValues] = useState<
    Partial<AssetFormValues>
  >({});
  const [financialValues, setFinancialValues] =
    useState<FinancialFormValues | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const errorSummaryRef = useRef<HTMLDivElement>(null);

  const form = useForm({
    resolver: zodResolver(assetSchema),
    defaultValues: {
      name: '',
      address: '',
      asset_type: 'single_family',
      self_managed: true,
      management_platform: 'none',
      units: 1,
      land_percentage: 0,
      investment_status: 'active',
    },
  });

  // Reset form when closed
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setCurrentStep('address');
      setSelectedAddress(null);
      setAssetData(null);
      setPropertyValues({});
      setFinancialValues(null);
      form.reset();
    }
    onOpenChange(newOpen);
  };

  // Handle address selection from the first step
  const handleAddressSelect = (address: AddressType) => {
    setSelectedAddress(address);
    form.setValue('address', address.formattedAddress);
  };

  // Handle asset found from address search
  const handleAssetFound = (asset: FormattedAsset | null) => {
    setAssetData(asset);
  };

  // Navigate to the next step
  const handleNext = () => {
    if (!selectedAddress) {
      toast.error('Please select a valid address before proceeding');
      return;
    }
    setCurrentStep('property');
  };

  // Handle the property details submission
  const handlePropertySubmit = (values: AssetFormValues) => {
    setPropertyValues(values);
    setCurrentStep('financial');
  };

  // Handle the financial details submission
  const handleFinancialSubmit = (financialValues: FinancialFormValues) => {
    setFinancialValues(financialValues);
    setCurrentStep('checklist');
  };

  // Handle the final submission with checklist
  const handleFinalSubmit = async () => {
    if (!financialValues) {
      toast.error('Financial details are required');
      return;
    }

    setIsLoading(true);
    setGeneralError(null);
    try {
      // Map financial form values to asset fields, filtering out non-asset fields
      const { has_mortgage, is_rental, ...assetFinancialFields } =
        financialValues;

      // Set loan fields to null if no mortgage
      if (!has_mortgage) {
        assetFinancialFields.loan_amount = null;
        assetFinancialFields.loan_due_date = null;
      }

      // Ensure empty date fields are sent as null
      if (assetFinancialFields.purchase_date === '') {
        assetFinancialFields.purchase_date = null;
      }
      if (assetFinancialFields.loan_due_date === '') {
        assetFinancialFields.loan_due_date = null;
      }

      const assetToCreate = {
        ...propertyValues,
        ...assetFinancialFields,
        address: selectedAddress?.formattedAddress,
      };

      console.log('Creating asset with data:', assetToCreate);

      await AssetService.createAsset(assetToCreate);
      toast.success('Asset created successfully');
      onOpenChange(false);
      if (onAssetAdded) {
        onAssetAdded();
      }
    } catch (error: any) {
      console.error('Error creating asset:', error);
      // Try to extract validation errors from backend
      if (error?.response?.data?.errors) {
        const errors = error.response.data.errors;
        let firstField: string | null = null;
        for (const [field, message] of Object.entries(errors)) {
          form.setError(field as keyof AssetFormValues, {
            message: String(message),
          });
          if (!firstField) firstField = field;
        }
        setGeneralError('Please fix the errors below before saving.');
        // Scroll to error summary
        setTimeout(() => {
          errorSummaryRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else if (error?.response?.data?.message) {
        setGeneralError(error.response.data.message);
        setTimeout(() => {
          errorSummaryRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        setGeneralError('Failed to create asset. Please try again.');
        setTimeout(() => {
          errorSummaryRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Go back to the previous step
  const handleBack = () => {
    if (currentStep === 'property') {
      setCurrentStep('address');
    } else if (currentStep === 'financial') {
      setCurrentStep('property');
    } else if (currentStep === 'checklist') {
      setCurrentStep('financial');
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 'address':
        return 'Search for the property address';
      case 'property':
        return 'Review and confirm property details';
      case 'financial':
        return 'Enter financial details';
      case 'checklist':
        return 'Review setup checklist and complete asset';
      default:
        return '';
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl md:max-w-2xl overflow-y-auto bg-white z-50"
      >
        <SheetHeader>
          <SheetTitle>Add New Asset</SheetTitle>
          <SheetDescription>{getStepDescription()}</SheetDescription>
        </SheetHeader>

        {/* Error summary at the top of the form */}
        {(generalError || Object.keys(form.formState.errors).length > 0) && (
          <div
            ref={errorSummaryRef}
            className="bg-red-100 border border-red-300 text-red-800 rounded-md px-4 py-3 mb-4"
            role="alert"
            tabIndex={-1}
          >
            {generalError || 'Please fix the errors below before saving.'}
          </div>
        )}

        {currentStep === 'address' ? (
          <Form {...form}>
            <form className="space-y-8">
              <AddressSearchStep
                onAddressSelect={handleAddressSelect}
                onNext={handleNext}
                isLoading={isLoading}
                onAssetFound={handleAssetFound}
              />
            </form>
          </Form>
        ) : currentStep === 'property' ? (
          <PropertyDetailsStep
            assetData={assetData}
            onNext={handlePropertySubmit}
            onBack={handleBack}
            isLoading={isLoading}
          />
        ) : currentStep === 'financial' ? (
          <FinancialDetailsStep
            parcelData={assetData?.parcelData}
            onNext={handleFinancialSubmit}
            onBack={handleBack}
            isLoading={isLoading}
          />
        ) : (
          <div className="space-y-6">
            <AssetChecklist
              assetType={propertyValues.asset_type}
              showProgress={true}
              compact={false}
            />

            <div className="flex justify-between pt-4">
              <button
                type="button"
                onClick={handleBack}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleFinalSubmit}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating Asset...' : 'Create Asset'}
              </button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
